import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Accessibility, Heart, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTrainerShellAssist } from '@/stores/trainerShellAssist.store';
import { getAppName, isLyrraOne } from '@/lib/platform';
import { BugReportFooterButton } from '@/components/support/BugReportDialog';

const footerAssistBtnClassDefault =
  'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--inst-player-primary,#0C1C38)] focus-visible:ring-offset-1 border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_22%,#e5e7eb)] bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,#fff)] text-[color:var(--inst-player-primary,#0C1C38)] hover:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_14%,#fff)] dark:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_35%,#334155)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_18%,#0f172a)] dark:text-white dark:hover:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_28%,#0f172a)]';

const footerAssistBtnClassLyrraOne = 'lo-footer-assist-btn';

export type FooterProps = {
  /**
   * Marque blanche (domaine custom / site formateur) :
   * copyright au nom du site, pas de chat support ni signalement bug LyrraOne.
   */
  whiteLabel?: boolean;
  /** Nom affiché dans le copyright (sinon nom d’app plateforme). */
  brandName?: string | null;
};

export function Footer({ whiteLabel = false, brandName }: FooterProps = {}) {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const setFooterAssistActive = useTrainerShellAssist((s) => s.setFooterAssistActive);
  const setA11yOpen = useTrainerShellAssist((s) => s.setA11yOpen);
  const setSupportChatOpen = useTrainerShellAssist((s) => s.setSupportChatOpen);
  const a11yOpen = useTrainerShellAssist((s) => s.a11yOpen);
  const supportChatOpen = useTrainerShellAssist((s) => s.supportChatOpen);
  const lyrraOneShell = isLyrraOne();
  const footerAssistBtnClass = lyrraOneShell ? footerAssistBtnClassLyrraOne : footerAssistBtnClassDefault;
  const footerAssistBtnActiveRing = lyrraOneShell
    ? 'ring-2 ring-[color:var(--lo-blue,#2563eb)] ring-offset-1'
    : 'ring-2 ring-[color:var(--inst-player-primary,#0C1C38)] ring-offset-1';

  const displayBrand = (brandName?.trim() || getAppName()).trim() || getAppName();
  /** Support / bug = outils plateforme Lyrra — masqués en marque blanche. */
  const showPlatformAssist = !whiteLabel;

  useEffect(() => {
    setFooterAssistActive(true);
    return () => {
      setFooterAssistActive(false);
      setA11yOpen(false);
      setSupportChatOpen(false);
    };
  }, [setFooterAssistActive, setA11yOpen, setSupportChatOpen]);

  const legalLinkClass = cn(
    'text-xs transition-colors',
    lyrraOneShell
      ? 'text-[color:var(--lo-text-tertiary,#64748b)] hover:text-[color:var(--lo-blue,#2563eb)]'
      : 'hover:text-[color:var(--inst-player-primary,#0C1C38)] dark:hover:text-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_85%,#fff)]',
  );

  return (
    <footer
      data-trainer-footer
      data-white-label={whiteLabel ? 'true' : undefined}
      className={cn(
        'flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t px-4 py-3 text-xs',
        lyrraOneShell
          ? 'lo-trainer-footer border-[color:var(--lo-border,#dce7f9)] bg-[color:var(--lo-surface,#ffffff)] text-[color:var(--lo-text-tertiary,#64748b)]'
          : 'border-[color:var(--inst-player-border,#e5e7eb)] bg-[color:var(--inst-player-surface,#ffffff)] text-[color:var(--inst-player-text-muted,#6b7280)] dark:border-[hsl(217,33%,22%)] dark:text-[#B6CAEC]',
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <p className="flex items-center gap-1 text-xs leading-normal">
          © {year} {displayBrand}.
          {!whiteLabel ? (
            <span className="hidden sm:inline">
              {t('footer.madeWith', 'Made with')}
              <Heart className="mx-0.5 inline h-3 w-3 fill-red-500 text-red-500" aria-hidden />
              {t('footer.forEducation', 'for education')}
            </span>
          ) : null}
        </p>

        <button
          type="button"
          onClick={() => setA11yOpen(!a11yOpen)}
          aria-expanded={a11yOpen}
          aria-label={t('accessibility.openCenter', "Ouvrir le centre d'accessibilité")}
          className={cn(footerAssistBtnClass, a11yOpen && footerAssistBtnActiveRing)}
        >
          <Accessibility className="h-4 w-4 shrink-0" aria-hidden />
          <span className="hidden sm:inline">{t('footer.a11yCenter', "Accessibilité")}</span>
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <nav className="flex items-center gap-4 text-xs" aria-label={t('footer.legal', 'Legal links')}>
          <Link to="/terms" className={legalLinkClass}>
            {t('footer.terms', 'Terms')}
          </Link>
          <Link to="/privacy" className={legalLinkClass}>
            {t('footer.privacy', 'Privacy')}
          </Link>
          {!whiteLabel ? (
            <Link to="/accessibility" className={legalLinkClass}>
              {t('footer.accessibility', 'Accessibility')}
            </Link>
          ) : (
            <Link to="/cookies" className={legalLinkClass}>
              {t('footer.cookies', 'Cookies')}
            </Link>
          )}
        </nav>

        {showPlatformAssist ? (
          <>
            <button
              type="button"
              onClick={() => setSupportChatOpen(!supportChatOpen)}
              aria-expanded={supportChatOpen}
              aria-label={t('support.chat.open', 'Ouvrir le chat support')}
              className={cn(
                footerAssistBtnClass,
                supportChatOpen && footerAssistBtnActiveRing,
              )}
            >
              <MessageCircle className="h-4 w-4 shrink-0" aria-hidden />
              <span className="hidden sm:inline">{t('footer.supportChat', 'Aide & support')}</span>
            </button>

            <BugReportFooterButton />
          </>
        ) : null}
      </div>
    </footer>
  );
}

export default Footer;
